import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import Pager from '../components/Pager';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 20;

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ amount: 0, commission: 0, fee: 0 });
  const [offset, setOffset] = useState(0);
  const [isNetwork, setIsNetwork] = useState(false);
  const [members, setMembers] = useState([]);

  const [search, setSearch] = useState('');
  const [type, setType] = useState(''); // '' | 'primary' | 'repeat'
  const [sourceClinicId, setSourceClinicId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('paid_date');
  const [order, setOrder] = useState('desc');

  const headers = { Authorization: 'Bearer ' + localStorage.getItem('clinicToken') };

  useEffect(() => {
    axios.get(`${API}/clinic-cabinet/me`, { headers }).then(r => {
      setIsNetwork(r.data.target_type === 'network');
      setMembers(r.data.members || []);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const fetchData = useCallback(() => {
    const params = {
      search: search || undefined,
      is_primary: type === 'primary' ? true : type === 'repeat' ? false : undefined,
      source_clinic_id: sourceClinicId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      sort, order, limit: LIMIT, offset,
    };
    axios.get(`${API}/clinic-cabinet/transactions`, { headers, params }).then(r => {
      setItems(r.data.items);
      setTotal(r.data.total);
      setSummary({ amount: r.data.summary_amount, commission: r.data.summary_commission, fee: r.data.summary_fee });
    }).catch(() => {});
    // eslint-disable-next-line
  }, [search, type, sourceClinicId, dateFrom, dateTo, sort, order, offset]);

  useEffect(() => { const t = setTimeout(fetchData, 250); return () => clearTimeout(t); }, [fetchData]);

  const toggleSort = (f) => {
    setOffset(0);
    if (sort === f) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(f); setOrder('asc'); }
  };
  const arrow = (f) => (sort !== f ? '' : order === 'asc' ? ' ▲' : ' ▼');

  const reset = () => {
    setSearch(''); setType(''); setSourceClinicId(''); setDateFrom(''); setDateTo(''); setOffset(0);
  };

  const cols = isNetwork ? '1fr 1.8fr 1.6fr 1.1fr 1.6fr 1fr 1fr 1fr 1fr' : '1fr 1.8fr 1.6fr 1.1fr 1fr 1fr 1fr 1fr';

  return (
    <Layout>
      <h1 style={s.h1}>Транзакции ({total})</h1>

      <div style={s.filterBar}>
        <input style={s.input} placeholder="Поиск по пациенту или врачу" value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }} />
        <select style={s.select} value={type} onChange={e => { setType(e.target.value); setOffset(0); }}>
          <option value="">Все типы</option>
          <option value="primary">Первичные</option>
          <option value="repeat">Повторные</option>
        </select>
        {isNetwork && (
          <select style={s.select} value={sourceClinicId} onChange={e => { setSourceClinicId(e.target.value); setOffset(0); }}>
            <option value="">Все учреждения</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <input type="date" style={s.input} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setOffset(0); }} />
        <span style={{ color: '#94a3b8' }}>—</span>
        <input type="date" style={s.input} value={dateTo} onChange={e => { setDateTo(e.target.value); setOffset(0); }} />
        <button style={s.btnGhost} onClick={reset}>Сброс</button>
      </div>

      <div style={s.summaryBar}>
        <div style={s.summaryItem}><span style={s.summaryLabel}>Сумма оплат</span><span style={s.summaryValue}>{fmtMoney(summary.amount)} ₽</span></div>
        <div style={s.summaryItem}><span style={s.summaryLabel}>Комиссия врачам</span><span style={s.summaryValue}>{fmtMoney(summary.commission)} ₽</span></div>
        <div style={s.summaryItem}><span style={s.summaryLabel}>Комиссия Refero</span><span style={s.summaryValue}>{fmtMoney(summary.fee)} ₽</span></div>
      </div>

      <div style={s.table}>
        <div style={{ ...s.head, gridTemplateColumns: cols }}>
          <div style={s.thClickable} onClick={() => toggleSort('paid_date')}>Дата оплаты{arrow('paid_date')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('patient_name')}>Пациент{arrow('patient_name')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('doctor_name')}>Врач{arrow('doctor_name')}</div>
          <div>ID врача</div>
          {isNetwork && <div>Учреждение</div>}
          <div style={s.thClickable} onClick={() => toggleSort('is_primary')}>Тип{arrow('is_primary')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('amount')}>Сумма{arrow('amount')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('commission_amount')}>Комиссия врачу{arrow('commission_amount')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('platform_fee')}>Комиссия Refero{arrow('platform_fee')}</div>
        </div>
        {items.map(t => (
          <div key={t.id} style={{ ...s.row, gridTemplateColumns: cols }}>
            <div style={s.muted}>{new Date(t.paid_date).toLocaleDateString('ru')}</div>
            <div style={s.strong}>{t.patient_name}</div>
            <div>{t.doctor_name}</div>
            <div style={s.refId}>{t.doctor_public_id || '—'}</div>
            {isNetwork && <div style={s.muted}>{t.source_clinic_name || '—'}</div>}
            <div><span style={{ ...s.badge, ...(t.is_primary ? s.badgeTeal : s.badgeGray) }}>{t.is_primary ? 'Первичная' : 'Повторная'}</span></div>
            <div style={s.money}>{fmtMoney(t.amount)} ₽</div>
            <div>{fmtMoney(t.commission_amount)} ₽</div>
            <div>{fmtMoney(t.platform_fee)} ₽</div>
          </div>
        ))}
        {items.length === 0 && <div style={s.empty}>{total === 0 ? 'Транзакций пока нет' : 'Ничего не найдено'}</div>}
      </div>
      <Pager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const s = {
  h1: { color: '#1B2B4B', fontSize: 24, fontWeight: 700, marginBottom: 20 },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  input: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' },
  select: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' },
  btnGhost: { background: 'transparent', color: '#64748b', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  summaryBar: { display: 'flex', gap: 24, background: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 16, boxShadow: '0 4px 16px rgba(27,43,75,0.05)' },
  summaryItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  summaryLabel: { color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 },
  summaryValue: { color: '#1B2B4B', fontSize: 18, fontWeight: 700 },
  table: { background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(27,43,75,0.05)' },
  head: { display: 'grid', padding: '14px 20px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7' },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center' },
  strong: { fontWeight: 600 },
  refId: { fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#94a3b8' },
  muted: { color: '#94a3b8', fontSize: 13 },
  money: { color: '#149e90', fontWeight: 600 },
  badge: { padding: '3px 10px', borderRadius: 20, fontSize: 12 },
  badgeTeal: { background: '#e9faf7', color: '#149e90' },
  badgeGray: { background: '#f1f5f9', color: '#64748b' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '40px 0' },
};
